import type { User, RolePermissionClaim } from './types';

function getPermissionCodename(rp: RolePermissionClaim): string {
  return rp.permissionDetail?.codename ?? rp.permission;
}

function normalizeLocationId(locationId: string | null | undefined): string | null {
  if (locationId === null || locationId === undefined) return null;
  const normalized = String(locationId).trim();
  return normalized.length > 0 ? normalized : null;
}

export function hasAllLocationAccess(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.allLocationAccess) return true;

  return user.accessRoles.some((ar) => (
    ar.scopeType === 'company'
    && ar.rolePermissions.some((rp) => (
      rp.scopeStrategy === 'all_locations'
      || (
        getPermissionCodename(rp) === 'company.full_access'
        && rp.scopeStrategy === 'company_only'
      )
    ))
  ));
}

export function getAccessibleLocationIds(user: User | null | undefined): string[] {
  if (!user) return [];

  const locationIds = new Set<string>();

  user.accessRoles.forEach((ar) => {
    const scopedLocationId = normalizeLocationId(ar.locationId);
    if (ar.scopeType === 'location' && scopedLocationId) {
      locationIds.add(scopedLocationId);
    }

    ar.rolePermissions.forEach((rp) => {
      if (rp.scopeStrategy === 'all_locations' && ar.scopeType === 'location' && scopedLocationId) {
        locationIds.add(scopedLocationId);
      }

      if (rp.scopeStrategy !== 'specific_locations') return;

      (rp.locations ?? []).forEach((location) => {
        const normalized = normalizeLocationId(location.locationId);
        if (normalized) locationIds.add(normalized);
      });
    });
  });

  return Array.from(locationIds);
}

export function hasPermission(
  user: User | null | undefined,
  codename: string,
  locationId?: string | null,
): boolean {
  if (!user) return false;

  for (const ar of user.accessRoles) {
    for (const rp of ar.rolePermissions) {
      const perm = getPermissionCodename(rp);

      // Company full access shortcut
      if (
        perm === 'company.full_access'
        && rp.scopeStrategy === 'company_only'
        && ar.scopeType === 'company'
      ) {
        return true;
      }

      if (perm !== codename) continue;

      if (rp.scopeStrategy === 'company_only') {
        if (ar.scopeType === 'company' && (locationId === null || locationId === undefined)) {
          return true;
        }
      }

      if (rp.scopeStrategy === 'all_locations') {
        if (ar.scopeType === 'company') return true;
        if (ar.scopeType === 'location' && ar.locationId && locationId && ar.locationId === locationId) {
          return true;
        }
      }

      if (rp.scopeStrategy === 'specific_locations') {
        const allowed = new Set((rp.locations ?? []).map((l) => l.locationId));
        if (ar.scopeType === 'company') {
          if (locationId && allowed.has(locationId)) return true;
        }
        if (ar.scopeType === 'location') {
          if (locationId && ar.locationId === locationId && allowed.has(locationId)) return true;
        }
      }
    }
  }

  return false;
}

export function hasAnyPermission(
  user: User | null | undefined,
  codenames: string[],
  locationId?: string | null,
): boolean {
  return codenames.some((codename) => hasPermission(user, codename, locationId));
}

export function getRoleLabels(user: User | null | undefined): string[] {
  if (!user) return [];
  const names = user.accessRoles.map((ar) => ar.roleName).filter(Boolean);
  return Array.from(new Set(names));
}
